import React, { useState, useEffect } from "react";
import Popup from "../Popup";
import GameOverScreen from "../GameOverScreen";
import { updateDocumentInSubcollection } from "../../firestore_sub";
import { useParams } from "react-router-dom";
import { auth } from "../../firebase";
import { updateDocument, getDocument } from "../../firestore";
import { MdOutlineRestartAlt } from "react-icons/md";
import rabbitImage from "./mole.svg";
import "./WhackAMole.css";

const WhackAMole = () => {
  const [score, setScore] = useState(0);
  const [timer, setTimer] = useState(60);
  const [gameOver, setGameOver] = useState(false);
  const [gameStarted, setGameStarted] = useState(true);
  const [molePositions, setMolePositions] = useState([]);
  const [showPopup, setShowPopup] = useState(false);
  const [lives, setLives] = useState(3);
  const uid = auth.currentUser ? auth.currentUser.uid : null;

  useEffect(() => {
    let countdown;
    let moleInterval;

    if (gameStarted && !gameOver && !showPopup) {
      countdown = setInterval(() => {
        setTimer((prevTimer) => {
          if (prevTimer <= 1) {
            clearInterval(countdown);
            setGameOver(true);
            return 60;
          }
          return prevTimer - 1;
        });
      }, 1000);

      moleInterval = setInterval(() => {
        if (!gameOver && !showPopup) {
          const newMolePositions = [];
          const numberOfMoles = Math.floor(Math.random() * 3) + 1;
          for (let i = 0; i < numberOfMoles; i++) {
            const random = Math.floor(Math.random() * 9);
            newMolePositions.push(random);
          }
          setMolePositions(newMolePositions);
        }
      }, 600);
    }

    return () => {
      clearInterval(countdown);
      clearInterval(moleInterval);
    };
  }, [gameStarted, gameOver, showPopup]);

  useEffect(() => {
    if (gameStarted && !gameOver && !showPopup) {
      const popupInterval = setInterval(() => {
        setShowPopup(true);
      }, 15000);

      return () => clearInterval(popupInterval);
    }
  }, [gameStarted, gameOver, showPopup]);

  async function checkGameStarted() {
    const queryParams = new URLSearchParams(window.location.search);
    var gameId = queryParams.get("id");
    const userId = auth.currentUser.uid;
    const subCollectionPath = `gameplay/${userId}/gameplays/`;
    const gameDoc = await getDocument(subCollectionPath, gameId);
    if (gameDoc.gameStarted) {
      window.location.href = `${window.location.origin}/instructions/2048`;
    }
  }

  async function gameOverDBUpdate() {
    const queryParams = new URLSearchParams(window.location.search);
    var gameId = queryParams.get("id");
    const userId = auth.currentUser.uid;
    const subCollectionPath = `gameplay/${userId}/gameplays/`;
    let currentScore = score;
    const gameplayData = {
      lives: lives,
      score: currentScore,
    };
    await updateDocument(subCollectionPath, gameId, gameplayData);
    await totalGameDataUpdate(currentScore);
  }

  async function totalGameDataUpdate(score) {
    const subCollectionPath = `gameplay`;
    const userId = auth.currentUser.uid;
    var docData = await getDocument(subCollectionPath, userId);
    let oldScore = docData.score;
    let total_games = docData.games_played + 1;
    let currentScore = score + oldScore;
    const gameplayData = {
      games_played: total_games,
      score: currentScore,
    };
    await updateDocument(subCollectionPath, userId, gameplayData);
  }

  useEffect(() => {
    if (lives <= 0) {
      gameOverDBUpdate();
      setGameOver(true);
    }
  }, [lives]);

  const restartGame = () => {
    setGameOver(false);
    setGameStarted(true);
    setScore(0);
    setTimer(60);
    setLives(3);
  };

  const exitGame = () => {
    setGameOver(true);
    setGameStarted(false);
    window.location.href = "/games";
  };

  const handleMoleClick = (index) => {
    if (gameStarted && !gameOver && molePositions.includes(index)) {
      setScore(score + 100);
      setMolePositions(molePositions.filter((moleIndex) => moleIndex !== index));
    }
  };

  const handlePopupClose = () => {
    setShowPopup(false);
  };

  const renderHearts = () => {
    return Array.from({ length: 3 }).map((_, index) => (
      <span
        key={index}
        className={`text-red-500 text-2xl ${
          index < lives ? "opacity-100" : "opacity-30"
        }`}
      >
        ❤️
      </span>
    ));
  };

  return (
    <div className="min-h-screen bg-[var(--background-color)] py-8 px-4 sm:px-6 lg:px-8 animate-fadeIn">
      <div className="max-w-7xl mx-auto">
        {/* Game Title with Gradient */}
        <h1 className="font-['Jersey_25',sans-serif] text-4xl md:text-5xl font-bold text-center mb-10 text-yellow-500 bg-gradient-to-r from-[var(--primary-color)] via-[var(--interactive-color)] to-[var(--accent-color)] text-transparent bg-clip-text transform transition-all">
          Whack A Rabbit
        </h1>

        {/* Game Stats and Controls */}
        <div className="text-white text-3xl w-full flex justify-between items-center p-4">
          <div className="flex items-center gap-6">
            <h1 className="text-2xl font-bold text-[var(--text-color)]">
              Score: {score}
            </h1>
            <h1 className="text-2xl font-bold text-[var(--text-color)]">
              Lives: {renderHearts()}
            </h1>
          </div>

          <div className="flex items-center gap-4">
            <button
              className="group relative inline-flex items-center px-6 py-3 bg-[var(--interactive-color)] text-white rounded-lg text-xl font-bold transform transition-all duration-300 hover:bg-opacity-90 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-[var(--interactive-color)] focus:ring-offset-2 focus:ring-offset-[var(--background-color)]"
              onClick={restartGame}
            >
              <MdOutlineRestartAlt className="mr-2 text-xl" />
              Restart
            </button>

            <button
              className="group relative inline-flex items-center px-6 py-3 bg-[var(--error-color)] text-white rounded-lg text-xl font-bold transform transition-all duration-300 hover:bg-opacity-90 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-[var(--error-color)] focus:ring-offset-2 focus:ring-offset-[var(--background-color)]"
              onClick={exitGame}
            >
              Exit
            </button>
          </div>
        </div>

        {/* Game Container with Hammer Cursor */}
        <div className="w-full max-w-4xl mx-auto cursor-hammer">
          <div className="grid grid-cols-3 gap-4 p-6 bg-green-800 rounded-2xl shadow-lg border-4 border-green-900">
            {Array.from({ length: 9 }).map((_, index) => (
              <div
                key={index}
                className="relative w-full h-32 bg-green-700 rounded-lg overflow-hidden"
              >
                {/* Grass */}
                <div className="absolute inset-x-0 bottom-0 h-12 bg-green-600"></div>

                {/* Sand Animation */}
                {molePositions.includes(index) && (
                  <div className="absolute inset-x-0 bottom-0 w-full h-12 bg-[rgb(67 43 43)] animate-sand"></div>
                )}

                {/* Rabbit (Mole) */}
                {molePositions.includes(index) && (
                  <img
                    src={rabbitImage}
                    alt="Rabbit"
                    className="absolute inset-x-0 bottom-0 w-1/2 h-28 mx-auto transition-transform duration-200 transform hover:scale-110 cursor-pointer"
                    onClick={() => handleMoleClick(index)}
                  />
                )}

                {/* Pot */}
                <div className="absolute inset-x-0 bottom-0 w-16 h-12 bg-brown-900 rounded-t-lg mx-auto"></div>
              </div>
            ))}
          </div>
        </div>

        {/* Popup and Game Over Screens */}
        {showPopup && (
          <Popup
            onClose={handlePopupClose}
            score={score}
            setScore={setScore}
            lives={lives}
            setLives={setLives}
            useLives={true}
            gameOver={gameOver}
          />
        )}

        {gameOver && gameStarted && (
          <GameOverScreen
            score={score}
            onRestart={restartGame}
            onExit={exitGame}
          />
        )}
      </div>
    </div>
  );
};

export default WhackAMole;